import React, { useState, useEffect, useRef } from 'react';

// CONTEXT

// COMPONENTS
import TooltipComponent from './tooltipComponent';
import AnimatedNumber from './AnimatedNumberComponent';

function CountdownComponent({ key, title, info, date, endDate, link, onFinish }) {
    const [timeLeft, setTimeLeft] = useState({});
    const [isEventInProgress, setIsEventInProgress] = useState(false);
    const [isEventFinished, setIsEventFinished] = useState(false);
    const [showFinished, setShowFinished] = useState(false);
    const [showOnlySeconds, setShowOnlySeconds] = useState(false);
    const finishedTimeout = useRef(null);
    const animationFrameRef = useRef(null);
    const lastUpdateTimeRef = useRef(Date.now());

    const targetDate = new Date(date).getTime();
    const eventEndDate = endDate ? new Date(endDate).getTime() : null;

    useEffect(() => {
        if (isEventFinished) {
            setShowFinished(true);
            finishedTimeout.current = setTimeout(() => {
                setShowFinished(false);
                if (onFinish) onFinish();
            }, 1000 * 60 * 60); // 1H
            return () => clearTimeout(finishedTimeout.current);
        }
    }, [isEventFinished, onFinish]);

    useEffect(() => {
        if (showFinished) return;

        const calculateTimeLeft = () => {
            const now = Date.now();
            const difference = targetDate - now;

            if (eventEndDate) {
                const isInProgress = now >= targetDate && now <= eventEndDate;
                setIsEventInProgress(isInProgress);

                if (now > eventEndDate && !isEventFinished) {
                    setIsEventFinished(true);
                }
            }

            if (difference <= 0) {
                setTimeLeft({
                    days: '00',
                    hours: '00',
                    minutes: '00',
                    seconds: '00'
                });
                setShowOnlySeconds(false);
            } else {
                const days = Math.floor(difference / (1000 * 60 * 60 * 24));
                const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((difference % (1000 * 60)) / 1000);

                // Vérifier si on doit afficher uniquement les secondes
                const totalSeconds = days * 86400 + hours * 3600 + minutes * 60 + seconds;
                setShowOnlySeconds(totalSeconds <= 59);

                setTimeLeft({
                    days: String(days).padStart(2, '0'),
                    hours: String(hours).padStart(2, '0'),
                    minutes: String(minutes).padStart(2, '0'),
                    seconds: String(seconds).padStart(2, '0'),
                });
            }

            // Vérifier si une seconde s'est écoulée
            const currentTime = Date.now();
            if (currentTime - lastUpdateTimeRef.current >= 1000) {
                lastUpdateTimeRef.current = currentTime;
            }

            animationFrameRef.current = requestAnimationFrame(calculateTimeLeft);
        };

        calculateTimeLeft();
        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, [targetDate, eventEndDate, isEventFinished, showFinished]);

    const content = (
        <TooltipComponent title={info || ''} placement="top">
            <div className="p-2 mt-2 w-full text-center flex flex-col gap-2 items-center bg-blanc-70 text-noir dark:bg-noir-70 dark:text-blanc rounded-2xl relative">
                <h3 className={`text-sm poppins-bold ${link ? 'underline' : ''}`}>{title}</h3>
                {isEventInProgress ? (
                    <div className="flex flex-col items-center gap-2">
                        <div className="flex items-center gap-2 bg-vert/10 dark:bg-green-500/10 text-vert dark:text-green-500 px-3 py-1.5 rounded-full text-xs poppins-medium border border-vert/20 dark:border-green-500/20">
                            <div className="size-2 rounded-full bg-vert dark:bg-green-500 animate-pulse"></div>
                            En cours
                        </div>
                        {endDate && (
                            <div className="text-xs poppins-medium text-noir/70 dark:text-blanc/70">
                                Jusqu'au {new Date(endDate).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'})}
                            </div>
                        )}
                    </div>
                ) : showFinished ? (
                    <div className="flex items-center gap-2 bg-rouge/10 dark:bg-red-500/10 text-rouge dark:text-red-500 px-3 py-1.5 rounded-full text-xs poppins-medium border border-rouge/20 dark:border-red-500/20">
                        <div className="size-2 rounded-full bg-rouge dark:bg-red-500"></div>
                        Terminé
                    </div>
                ) : (
                    <div className="poppins-semibold flex flex-wrap justify-center space-x-5 md:space-x-2">
                        {showOnlySeconds ? (
                            <div className="flex flex-col items-center w-16 md:w-20">
                                <div className="digit-container p-2 text-2xl lg:text-3xl h-12 md:h-14 w-full flex items-center justify-center">
                                    <AnimatedNumber value={timeLeft.seconds} />
                                </div>
                                <span className="poppins-light text-xs mt-2">Secondes</span>
                            </div>
                        ) : (
                            <>
                                {/* Jours */}
                                <div className="flex flex-col items-center w-8 md:w-10">
                                    <div className="digit-container p-2 text-sm lg:text-base h-8 md:h-10 w-full flex items-center justify-center">
                                        <AnimatedNumber value={timeLeft.days} />
                                    </div>
                                    <span className="poppins-light text-xxs mt-2">Jours</span>
                                </div>
                                {/* Heures */}
                                <div className="flex flex-col items-center w-8 md:w-10">
                                    <div className="digit-container p-2 text-sm lg:text-base h-8 md:h-10 w-full flex items-center justify-center">
                                        <AnimatedNumber value={timeLeft.hours} />
                                    </div>
                                    <span className="poppins-light text-xxs mt-2">Heures</span>
                                </div>
                                {/* Minutes */}
                                <div className="flex flex-col items-center w-8 md:w-10">
                                    <div className="digit-container p-2 text-sm lg:text-base h-8 md:h-10 w-full flex items-center justify-center">
                                        <AnimatedNumber value={timeLeft.minutes} />
                                    </div>
                                    <span className="poppins-light text-xxs mt-2">Minutes</span>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        </TooltipComponent>
    );

    return link ? (
        <a href={link} target="_blank" rel="noopener noreferrer" className="block">
            {content}
        </a>
    ) : content;
}

export default CountdownComponent;